/**
 * SegmentedControl — tab/pill segmented control with animated indicator.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §17
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { View, Text, type LayoutChangeEvent } from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  useReducedMotion,
} from 'react-native-reanimated'
import { selection } from '@/lib/utils/haptics'
import { Pressable } from './Pressable'
import { GlassSurface } from './GlassSurface'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING, INTERACTION } from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import { cn } from '../../lib/theme/variants'

// ─── Types ──────────────────────────────────────────────────────────────────

interface SegmentedControlOption<T extends string = string> {
  value: T
  label: string
  badge?: number
}

interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Visual style */
  variant?: 'default' | 'pills'
  /** Full width vs content width */
  fullWidth?: boolean
  disabled?: boolean
  /** Additional NativeWind className */
  className?: string
  /** E2E selector prefix for segment buttons. */
  testIDPrefix?: string
}

// NOTE: Animated sliding indicator only active when fullWidth=true (non-fullWidth
// uses static background due to variable-width tabs requiring per-tab measurement)

// ─── Spring config ──────────────────────────────────────────────────────────
//
// Near-critically damped so the thumb still slides, without overshooting the
// neighbouring label.

const INDICATOR_SPRING = { damping: 26, stiffness: 170 }

/**
 * The name a screen reader reads for a segment.
 *
 * Without it these render as an unnamed `tab` — verified in the accessibility
 * tree, where every other control on the screen had a name and these two had
 * none. The label text lives in a child `Text`, and a Pressable carrying an
 * explicit `accessibilityRole` does not reliably inherit its name from that
 * child on web, so VoiceOver announced position and selection with nothing to
 * identify which segment it was on.
 *
 * The badge is part of the name because it is part of the meaning: "Viajes, 3"
 * is what the control says visually, so it is what it should say out loud.
 */
function segmentA11yLabel<T extends string>(option: SegmentedControlOption<T>): string {
  return option.badge != null ? `${option.label}, ${option.badge}` : option.label
}

// ─── SegmentedControl ───────────────────────────────────────────────────────

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  variant = 'default',
  fullWidth = true,
  disabled = false,
  className,
  testIDPrefix,
}: SegmentedControlProps<T>) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  // The thumb follows MEASURED segments, not a geometric fraction.
  //
  // It used to be `containerWidth / options.length` — an exact third — while
  // the segments themselves were laid out by flex. Those two agree only while
  // every label fits its third. Yoga gives a flex item an automatic minimum
  // size of its own min-content width, so one long word ("Anteriores" at 2x
  // system text) makes its segment wider than a third and squeezes the others
  // narrower; the thumb kept painting thirds and slid over the neighbouring
  // label. Measuring removes the assumption instead of tuning it.
  const segmentLayouts = useRef<Record<number, { x: number; width: number }>>({})
  const indicatorX = useSharedValue(0)
  const indicatorWidth = useSharedValue(0)
  const measured = useSharedValue(0)

  const syncIndicator = useCallback(
    (animate: boolean) => {
      const idx = Math.max(
        options.findIndex((o) => o.value === value),
        0,
      )
      const target = segmentLayouts.current[idx]
      const first = segmentLayouts.current[0]
      if (!target || !first) return
      // Offsets are taken relative to the first segment so this does not
      // depend on whether `onLayout` x includes the container's own padding.
      const x = target.x - first.x
      // First paint and reduced motion snap to the measured rect so the
      // thumb never overshoots into a neighbour, and layout does not jump.
      if (measured.value === 0 || !animate || reducedMotion) {
        indicatorX.value = x
        indicatorWidth.value = target.width
      } else {
        indicatorX.value = withSpring(x, INDICATOR_SPRING)
        indicatorWidth.value = withSpring(target.width, INDICATOR_SPRING)
      }
      measured.value = 1
    },
    [options, value, indicatorX, indicatorWidth, measured, reducedMotion],
  )

  // Sync indicator when value changes (covers both press and external updates)
  useEffect(() => {
    syncIndicator(true)
  }, [syncIndicator])

  const handleSegmentLayout = useCallback(
    (index: number) => (e: LayoutChangeEvent) => {
      const { x, width } = e.nativeEvent.layout
      const prev = segmentLayouts.current[index]
      if (prev && prev.x === x && prev.width === width) return
      segmentLayouts.current[index] = { x, width }
      syncIndicator(false)
    },
    [syncIndicator],
  )

  const handlePress = useCallback(
    (option: SegmentedControlOption<T>) => {
      if (disabled) return
      onChange(option.value)
      void selection()
    },
    [disabled, onChange],
  )

  const indicatorStyle = useAnimatedStyle(() => ({
    width: indicatorWidth.value,
    transform: [{ translateX: indicatorX.value }],
    // Hide until the segments are measured to prevent a flash of zero-width
    // indicator on first paint.
    opacity: measured.value,
  }))

  // ─── Pills variant ──────────────────────────────────────────────────────

  if (variant === 'pills') {
    return (
      <View
        className={cn('flex-row gap-2', !fullWidth && 'self-start', className)}
        accessibilityRole="tablist"
      >
        {options.map((option) => {
          const isActive = option.value === value
          return (
            <Pressable
              key={option.value}
              onPress={() => handlePress(option)}
              disabled={disabled}
              accessibilityRole="tab"
              accessibilityLabel={segmentA11yLabel(option)}
              accessibilityState={{ selected: isActive }}
              testID={testIDPrefix ? `${testIDPrefix}-${option.value}` : undefined}
              style={{
                paddingHorizontal: SPACING.md,
                paddingVertical: SPACING.sm,
                minHeight: INTERACTION.minTouchTarget,
                justifyContent: 'center',
                borderRadius: RADIUS.full,
                backgroundColor: isActive ? COLORS.coral.DEFAULT : 'transparent',
                borderWidth: 1.5,
                borderColor: COLORS.coral.DEFAULT,
                opacity: disabled ? 0.4 : 1,
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: isActive ? '#FFFFFF' : colors.textAccent,
                }}
              >
                {option.label}
              </Text>
            </Pressable>
          )
        })}
      </View>
    )
  }

  // ─── Default variant — shared container with sliding indicator ──────────

  return (
    <View className={cn(!fullWidth && 'self-start', className)}>
      <GlassSurface
        tone="regular"
        radius={RADIUS.md}
        style={{
          flexDirection: 'row',
          opacity: disabled ? 0.4 : 1,
        }}
        accessibilityRole="tablist"
        contentStyle={{
          flexDirection: 'row',
          padding: RADIUS.xs,
        }}
      >
        {/* Animated sliding indicator.
          The thumb is brand purple in BOTH themes. It used to be
          `isDark ? colors.surfaceRaised : '#FFFFFF'` — which reads as a filled
          purple pill in dark but as a white pill on a near-white track in
          light, where the selection all but disappeared. One fill, one
          on-brand ink, same affordance in either theme. */}
        {fullWidth && (
          <Animated.View
            // The sliding indicator is painted UNDER the tabs only because each
            // tab carries `zIndex: 1` below. That zIndex reads as cosmetic, and
            // deleting it would make this absolute sibling eat every tab tap on
            // web (CSS paints positioned elements above in-flow siblings
            // regardless of DOM order). Declaring it non-interactive removes the
            // dependency on a number that looks free to delete.
            pointerEvents="none"
            style={[
              {
                position: 'absolute',
                top: RADIUS.xs,
                left: RADIUS.xs,
                bottom: RADIUS.xs,
                borderRadius: RADIUS.md - 2,
                backgroundColor: COLORS.purple.DEFAULT,
              },
              indicatorStyle,
            ]}
          />
        )}

        {options.map((option, index) => {
          const isActive = option.value === value
          return (
            <Pressable
              key={option.value}
              onPress={() => handlePress(option)}
              onLayout={handleSegmentLayout(index)}
              disabled={disabled}
              accessibilityRole="tab"
              accessibilityLabel={segmentA11yLabel(option)}
              accessibilityState={{ selected: isActive }}
              testID={testIDPrefix ? `${testIDPrefix}-${option.value}` : undefined}
              style={{
                flex: fullWidth ? 1 : undefined,
                paddingHorizontal: fullWidth ? undefined : SPACING.md + SPACING.xs,
                paddingVertical: SPACING.sm,
                // 8px of padding around a 17px line box is a 33px tab. These are
                // the primary filter on the trips screen, so they get a real one.
                minHeight: INTERACTION.minTouchTarget,
                borderRadius: RADIUS.md - 2,
                // Non-fullWidth: static background (no animated indicator).
                // Same brand fill as the sliding thumb above.
                backgroundColor: !fullWidth && isActive ? COLORS.purple.DEFAULT : 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                zIndex: 1,
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: isActive ? FONTS.bodyBold : FONTS.body,
                  // The thumb is a fixed brand fill, so the active label needs a
                  // fixed light ink — a theme-aware token would go dark on it.
                  color: isActive ? '#FFFFFF' : colors.textSecondary,
                }}
              >
                {option.label}
              </Text>
              {option.badge !== undefined && option.badge > 0 && (
                <View
                  style={{
                    marginLeft: SPACING.xs + 2,
                    backgroundColor: COLORS.coral.dark,
                    borderRadius: RADIUS.md,
                    minWidth: 18,
                    height: 18,
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingHorizontal: 5 /* sub-token: tight badge padding */,
                  }}
                >
                  <Text style={{ color: '#FFFFFF', fontSize: 12, fontFamily: FONTS.bodyBold }}>
                    {option.badge}
                  </Text>
                </View>
              )}
            </Pressable>
          )
        })}
      </GlassSurface>
    </View>
  )
}
